import React from 'react'
import ServerTable from '../components/ServerTable'

const DEFAULT_PAGE_SIZE = 40

export default function VehiclesIndexPage({ initialData = [] }) {
  const initialPayload = React.useMemo(() => normalizeVehiclePayload(initialData), [initialData])

  const [data, setData] = React.useState(initialPayload.records)
  const [rowCount, setRowCount] = React.useState(initialPayload.meta.total_count)
  const [pageCount, setPageCount] = React.useState(initialPayload.meta.total_pages)
  const [isLoading, setIsLoading] = React.useState(false)
  const [error, setError] = React.useState('')
  const [modalState, setModalState] = React.useState({
    isOpen: false,
    images: [],
    index: 0,
    title: '',
  })
  const abortControllerRef = React.useRef(null)

  const openImageModal = React.useCallback((images, startIndex, title) => {
    if (!Array.isArray(images) || images.length === 0) return

    setModalState({
      isOpen: true,
      images,
      index: Math.max(0, Math.min(startIndex, images.length - 1)),
      title,
    })
  }, [])

  const closeImageModal = React.useCallback(() => {
    setModalState((old) => ({ ...old, isOpen: false }))
  }, [])

  const showPreviousImage = React.useCallback(() => {
    setModalState((old) => {
      if (!old.images.length) return old
      return {
        ...old,
        index: (old.index - 1 + old.images.length) % old.images.length,
      }
    })
  }, [])

  const showNextImage = React.useCallback(() => {
    setModalState((old) => {
      if (!old.images.length) return old
      return {
        ...old,
        index: (old.index + 1) % old.images.length,
      }
    })
  }, [])

  React.useEffect(() => {
    if (!modalState.isOpen) return undefined

    const handleKeyDown = (event) => {
      if (event.key === 'Escape') closeImageModal()
      if (event.key === 'ArrowLeft') showPreviousImage()
      if (event.key === 'ArrowRight') showNextImage()
    }

    window.addEventListener('keydown', handleKeyDown)
    return () => window.removeEventListener('keydown', handleKeyDown)
  }, [modalState.isOpen, closeImageModal, showPreviousImage, showNextImage])

  const columns = React.useMemo(() => {
    const makeOptions = uniqueStringOptions(data, 'make')
    const modelOptions = uniqueStringOptions(data, 'model')
    const yearOptions = uniqueYearOptions(data)

    return [
      { accessorFn: (row) => row.id?.toString() || '', id: 'id', header: 'ID' },
      { accessorFn: (row) => row.invoice_id?.toString() ? `CE${row.invoice_id}` : '', id: 'invoice_id', header: 'Invoice' },
      {
        accessorKey: 'sales_rep_name',
        header: 'Sales Rep Name',
        enableSorting: false,
      },
      { accessorKey: 'vin_number', header: 'VIN Number' },
      { accessorKey: 'stock_number', header: 'Stock Number' },
      {
        accessorKey: 'make',
        header: 'Make',
        meta: {
          filterVariant: 'select',
          selectOptions: makeOptions,
        },
      },
      {
        accessorKey: 'model',
        header: 'Model',
        meta: {
          filterVariant: 'select',
          selectOptions: modelOptions,
        },
      },
      {
        accessorFn: (row) => row.year?.toString() || '',
        id: 'year',
        header: 'Year',
        meta: {
          filterVariant: 'select',
          selectOptions: yearOptions,
        },
      },
      {
        accessorFn: (row) => row.before_images || [],
        id: 'before_images',
        header: 'Before Images',
        meta: {
          filterVariant: 'select',
          selectOptions: ['With Images', 'No Images'],
        },
        enableSorting: false,
        cell: ({ row }) => (
          <ThumbnailStrip
            images={row.original.before_images || []}
            label="Before"
            onOpen={openImageModal}
          />
        ),
      },
      {
        accessorFn: (row) => row.after_images || [],
        id: 'after_images',
        header: 'After Images',
        meta: {
          filterVariant: 'select',
          selectOptions: ['With Images', 'No Images'],
        },
        enableSorting: false,
        cell: ({ row }) => (
          <ThumbnailStrip
            images={row.original.after_images || []}
            label="After"
            onOpen={openImageModal}
          />
        ),
      },
      {
        accessorKey: 'line_items',
        header: 'Line Items',
        enableSorting: false,
      },
      { accessorKey: 'exterior_color', header: 'Exterior Color' },
      // { accessorFn: (row) => row.override_price?.toString() || '', id: 'override_price', header: 'Override Price' },
      // { accessorFn: (row) => row.disabled?.toString() || '', id: 'disabled', header: 'Disabled' },
      { accessorFn: (row) => row.created_at || '', id: 'created_at', header: 'Created At' },
      // { accessorKey: 'package_type', header: 'Package Type' },
      // { accessorFn: (row) => row.installed?.toString() || '', id: 'installed', header: 'Installed' },
      // {
      //   accessorFn: (row) => row.prices_overriden_by_rep?.toString() || '',
      //   id: 'prices_overriden_by_rep',
      //   header: 'Prices Overriden By Rep',
      // },
    ]
  }, [data, openImageModal])

  const loadVehicles = React.useCallback(async ({ columnFilters = [], pagination = { pageIndex: 0, pageSize: DEFAULT_PAGE_SIZE }, sorting = [] } = {}) => {
    const filters = columnFilters.reduce((acc, filter) => {
      const value = typeof filter.value === 'string' ? filter.value.trim() : filter.value
      if (value === '' || value === null || value === undefined) return acc
      acc[filter.id] = value
      return acc
    }, {})

    const params = new URLSearchParams()
    params.set('page', String((pagination.pageIndex || 0) + 1))
    params.set('page_size', String(pagination.pageSize || DEFAULT_PAGE_SIZE))

    if (Array.isArray(sorting) && sorting.length > 0) {
      params.set('sorting', JSON.stringify(sorting))
    }

    if (Object.keys(filters).length > 0) {
      params.set('filters', JSON.stringify(filters))
    }

    if (abortControllerRef.current) {
      abortControllerRef.current.abort()
    }
    const abortController = new AbortController()
    abortControllerRef.current = abortController

    setIsLoading(true)
    setError('')

    try {
      const response = await fetch(`/vehicles.json?${params.toString()}`, {
        headers: {
          Accept: 'application/json',
        },
        signal: abortController.signal,
      })

      if (!response.ok) {
        throw new Error(`Unable to load vehicles: ${response.status}`)
      }

      const json = await response.json()
      const payload = normalizeVehiclePayload(json)
      setData(payload.records)
      setRowCount(payload.meta.total_count)
      setPageCount(payload.meta.total_pages)
    } catch (err) {
      if (err.name === 'AbortError') return
      setError(err.message)
      setData([])
      setRowCount(0)
      setPageCount(0)
    } finally {
      setIsLoading(false)
    }
  }, [])

  return (
    <div>
      <h1>Vehicles</h1>
      {error ? <div className="alert alert-danger">{error}</div> : null}
      {isLoading ? <div>Loading vehicles...</div> : null}
      <ServerTable
        columns={columns}
        data={data}
        initialSorting={[{ id: 'created_at', desc: true }]}
        onQueryChange={loadVehicles}
        rowCount={rowCount}
        pageCount={pageCount}
        initialPagination={{
          pageIndex: Math.max((initialPayload.meta.page || 1) - 1, 0),
          pageSize: initialPayload.meta.page_size || DEFAULT_PAGE_SIZE,
        }}
        exportFileName="vehicles"
      />
      <ImageCarouselModal
        isOpen={modalState.isOpen}
        images={modalState.images}
        currentIndex={modalState.index}
        title={modalState.title}
        onClose={closeImageModal}
        onPrev={showPreviousImage}
        onNext={showNextImage}
      />
    </div>
  )
}

function ThumbnailStrip({ images, label, onOpen }) {
  if (!images.length) return <span className="text-muted">No Images</span>

  const visible = images.slice(0, 3)
  const remaining = images.length - visible.length

  return (
    <div className="d-flex align-items-center gap-1">
      {visible.map((url, index) => (
        <button
          key={`${label}-${url}-${index}`}
          type="button"
          onClick={() => onOpen(images, index, label)}
          style={{
            border: 'none',
            padding: 0,
            background: 'transparent',
            lineHeight: 0,
          }}
          title={`Open ${label} image ${index + 1}`}
        >
          <img
            src={url}
            alt={`${label} ${index + 1}`}
            style={{
              width: '28px',
              height: '28px',
              objectFit: 'cover',
              borderRadius: '4px',
              border: '1px solid #d0d0d0',
            }}
          />
        </button>
      ))}
      {remaining > 0 ? <span style={{ fontSize: '12px' }}>+{remaining}</span> : null}
    </div>
  )
}

function ImageCarouselModal({ isOpen, images, currentIndex, title, onClose, onPrev, onNext }) {
  if (!isOpen || !images.length) return null

  const activeImage = images[currentIndex]

  return (
    <div
      role="dialog"
      aria-modal="true"
      onClick={onClose}
      style={{
        position: 'fixed',
        inset: 0,
        backgroundColor: 'rgba(0, 0, 0, 0.72)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 1050,
        padding: '20px',
      }}
    >
      <div
        onClick={(event) => event.stopPropagation()}
        style={{
          width: 'min(1000px, 100%)',
          background: '#fff',
          borderRadius: '10px',
          overflow: 'hidden',
          boxShadow: '0 16px 40px rgba(0,0,0,0.35)',
        }}
      >
        <div className="d-flex justify-content-between align-items-center p-2 border-bottom">
          <strong>{title} Images</strong>
          <button type="button" className="btn btn-sm btn-outline-secondary" onClick={onClose}>Close</button>
        </div>
        <div className="d-flex align-items-center justify-content-between p-2">
          <button type="button" className="btn btn-outline-secondary" onClick={onPrev}>Prev</button>
          <div style={{ width: '100%', textAlign: 'center', padding: '0 12px' }}>
            <img
              src={activeImage}
              alt={`${title} ${currentIndex + 1}`}
              style={{
                maxWidth: '100%',
                maxHeight: '70vh',
                objectFit: 'contain',
              }}
            />
            <div style={{ marginTop: '8px', fontSize: '13px', color: '#555' }}>
              {currentIndex + 1} of {images.length}
            </div>
          </div>
          <button type="button" className="btn btn-outline-secondary" onClick={onNext}>Next</button>
        </div>
      </div>
    </div>
  )
}

function uniqueStringOptions(records, key) {
  return Array.from(
    new Set(
      (records || [])
        .map((row) => (row?.[key] ?? '').toString().trim())
        .filter((value) => value.length > 0),
    ),
  ).sort((a, b) => a.localeCompare(b))
}

function uniqueYearOptions(records) {
  return Array.from(
    new Set(
      (records || [])
        .map((row) => row?.year)
        .filter((value) => value !== null && value !== undefined && String(value).trim() !== '')
        .map((value) => String(value)),
    ),
  ).sort((a, b) => Number(b) - Number(a))
}

function normalizeVehiclePayload(payload) {
  if (Array.isArray(payload)) {
    return {
      records: payload,
      meta: {
        total_count: payload.length,
        total_pages: 1,
        page: 1,
        page_size: DEFAULT_PAGE_SIZE,
      },
    }
  }

  const records = Array.isArray(payload?.records) ? payload.records : []
  const meta = payload?.meta || {}
  const totalCount = Number(meta.total_count)
  const totalPages = Number(meta.total_pages)
  const page = Number(meta.page)
  const pageSize = Number(meta.page_size)

  return {
    records,
    meta: {
      total_count: Number.isFinite(totalCount) ? totalCount : records.length,
      total_pages: Number.isFinite(totalPages) ? totalPages : 1,
      page: Number.isFinite(page) ? page : 1,
      page_size: Number.isFinite(pageSize) ? pageSize : DEFAULT_PAGE_SIZE,
    },
  }
}
